/* eslint-disable no-new */
import { Wrapper, useMapDot } from "nystem-components";
import { useEffect, useRef, useState } from "react";
import { Point } from "ol/geom.js";
import { Map, View } from "ol/index.js";
import ImageLayer from "ol/layer/Image.js";
import ImageStatic from "ol/source/ImageStatic.js";
import { OSM } from "ol/source.js";
import { Tile as TileLayer } from "ol/layer.js";
import { useGeographic } from "ol/proj.js";
import "ol/ol.css";

// eslint-disable-next-line react-hooks/rules-of-hooks
useGeographic();

const MapLantmateriet = ({ value, model }) => {
  const mapRef = useRef();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const target = mapRef.current;

    const { coordinate = [13.012, 55.587], zoom = 11 } = value || {};

    const point = new Point(coordinate);
    const baseLayer = new TileLayer({
      source: new OSM({
        wrapX: false,
      }),
    });
    const previewLayer = new ImageLayer({ visible: false, opacity: 1 });
    const imageLayer = new ImageLayer({ opacity: 1 });

    const map = new Map({
      layers: [baseLayer, previewLayer, imageLayer],
      view: new View({ center: coordinate, zoom }),
      target,
    });

    useMapDot({
      type: "default",
      points: { coordinate: point, color: "blue", shape: "dot" },
      map,
      tileLayer: imageLayer,
    });

    let canceled = false;
    let renderTimeout;
    let updateViewportImage;
    let currentObjectUrl;
    let lastRenderUrl = "";
    let renderRequest = 0;
    const loadImage = async () => {
      const response = await fetch(
        `/lantmateriet/ortofoto?lon=${coordinate[0]}&lat=${coordinate[1]}`,
      );

      if (response.status === 202) {
        if (!canceled) renderTimeout = setTimeout(loadImage, 1000);
        return;
      }

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }

      const { imageUrl, extent, tiffUrl } = await response.json();
      if (canceled || !extent || !imageUrl) return;

      const previewSource = new ImageStatic({
        url: imageUrl,
        imageExtent: extent,
        projection: "EPSG:4326",
        interpolate: false,
      });
      previewLayer.setSource(previewSource);
      previewLayer.setVisible(false);
      map.getView().setCenter(coordinate);
      map.getView().setZoom(zoom);

      updateViewportImage = () => {
        if (canceled) return;
        const size = map.getSize();
        if (!size) return;
        const pixelRatio = Math.max(1, Math.ceil(window.devicePixelRatio || 1));
        const viewExtent = map.getView().calculateExtent(size);
        const renderUrl =
          `/lantmateriet/ortofoto/render.png?lon=${coordinate[0]}&lat=${coordinate[1]}` +
          `&bbox=${viewExtent.join(",")}&width=${size[0] * pixelRatio}&height=${size[1] * pixelRatio}`;
        if (renderUrl === lastRenderUrl) return;
        lastRenderUrl = renderUrl;
        const requestId = ++renderRequest;
        setIsLoading(true);
        fetch(renderUrl)
          .then(async (renderResponse) => {
            if (!renderResponse.ok)
              throw new Error(await renderResponse.text());

            const blob = await renderResponse.blob();
            if (canceled || requestId !== renderRequest) return;
            const nextObjectUrl = URL.createObjectURL(blob);
            await new Promise((resolve, reject) => {
              const image = new Image();
              image.onload = () => resolve();
              image.onerror = reject;
              image.src = nextObjectUrl;
            });

            const source = new ImageStatic({
              url: nextObjectUrl,
              imageExtent: viewExtent,
              projection: "EPSG:4326",
              interpolate: false,
            });
            source.on("imageloadend", () => {
              previewLayer.setVisible(false);
              setIsLoading(false);
              if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
              currentObjectUrl = nextObjectUrl;
            });
            source.on("imageloaderror", () => {
              previewLayer.setVisible(false);
              setIsLoading(false);
            });
            imageLayer.setSource(source);
          })
          .catch((error) => {
            if (requestId !== renderRequest) return;
            previewLayer.setVisible(false);
            setIsLoading(false);
            console.error("Lantmateriet render fetch error", renderUrl, error);
          });
      };

      updateViewportImage();
      map.on("moveend", updateViewportImage);
    };

    loadImage().catch((error) => {
      console.error("Failed to load Ortofoto Nedladdning image", error);
    });

    return () => {
      canceled = true;
      if (renderTimeout) clearTimeout(renderTimeout);
      if (updateViewportImage) map.un("moveend", updateViewportImage);
      if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
      setIsLoading(false);
      map.setTarget(null);
      target.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative">
      <Wrapper ref={mapRef} className={model.className}></Wrapper>
      {isLoading && (
        <div className="pointer-events-none absolute right-3 top-3 z-10 rounded-md bg-white/90 px-2.5 py-1.5 text-xs leading-tight text-neutral-800 shadow">
          Loading ortofoto...
        </div>
      )}
    </div>
  );
};

export default MapLantmateriet;
